import React, { useState } from "react";

const microsToDollars = (micros) =>
  micros == null ? "—" : `$${(Number(micros) / 1_000_000).toFixed(2)}`;

const formatPercent = (v) =>
  v == null ? "—" : `${(Number(v) * 100).toFixed(2)}%`;

const formatDate = (v) => {
  if (v == null) return "—";
  try {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
};

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

const METRICS_ROWS = [
  { key: "impressions", label: "Impressions" },
  { key: "clicks", label: "Clicks" },
  { key: "cost_micros", label: "Cost", format: microsToDollars },
  { key: "ctr", label: "CTR", format: formatPercent },
  { key: "average_cpc_micros", label: "Avg. CPC", format: microsToDollars },
  { key: "conversions", label: "Conversions" },
  { key: "conversions_value", label: "Conversions value", format: (v) => (v != null ? `$${Number(v).toFixed(2)}` : "—") },
];

// Show Sync when entity is published and in active/enabled/paused state
const canSyncCampaign = (c) =>
  Boolean(c?.google_campaign_id) &&
  (String(c?.status || "").toUpperCase() === "ENABLED" || String(c?.status || "").toUpperCase() === "PAUSED");
const canSyncAdGroup = (ag) =>
  Boolean(ag?.google_ad_group_id) &&
  (String(ag?.status || "").toUpperCase() === "ENABLED" || String(ag?.status || "").toUpperCase() === "PAUSED");
const canSyncAd = (ad) =>
  Boolean(ad?.google_ad_id) &&
  (ad?.status === "published" || ad?.status === "paused");

function InsightsTable({ insights, emptyLabel }) {
  if (!insights) {
    return <p className="campaign-detail-page__performance-empty">{emptyLabel}</p>;
  }
  return (
    <div className="campaign-detail-page__performance-table-wrap">
      <table className="campaign-detail-page__performance-table table">
        <thead>
          <tr>
            <th className="campaign-detail-page__performance-th">Metric</th>
            <th className="campaign-detail-page__performance-th campaign-detail-page__performance-th--value">Value</th>
          </tr>
        </thead>
        <tbody>
          {METRICS_ROWS.map(({ key, label, format }) => {
            const raw = insights[key];
            const value = format ? format(raw) : (raw ?? "—");
            return (
              <tr key={key} className="campaign-detail-page__performance-tr">
                <td className="campaign-detail-page__performance-td campaign-detail-page__performance-td--label">{label}</td>
                <td className="campaign-detail-page__performance-td campaign-detail-page__performance-td--value">{value}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SectionHeader({ title, lastSynced, onSync, syncing, showSync }) {
  return (
    <div className="campaign-detail-page__performance-header">
      <div className="campaign-detail-page__performance-header-left">
        <h5 className="campaign-detail-page__performance-title">{title}</h5>
        {lastSynced && (
          <span className="campaign-detail-page__performance-synced">
            Last synced: {formatDate(lastSynced)}
          </span>
        )}
      </div>
      {showSync && (
        <div className="campaign-detail-page__performance-header-right">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm campaign-detail-page__sync-btn"
            onClick={onSync}
            disabled={syncing}
          >
            {syncing ? "Syncing…" : "Sync Analytics"}
          </button>
        </div>
      )}
    </div>
  );
}

const GoogleCampaignPerformancePanel = ({ campaign, onRefetch, toast }) => {
  const [syncingCampaign, setSyncingCampaign] = useState(false);
  const [syncingAdGroupId, setSyncingAdGroupId] = useState(null);
  const [syncingAdId, setSyncingAdId] = useState(null);

  const hasGoogleCampaignId = Boolean(campaign?.google_campaign_id);
  const campaignInsights = campaign?.campaign_insights ?? null;
  const adGroups = campaign?.ad_groups ?? [];

  const syncCampaign = async () => {
    if (!campaign?.id || !hasGoogleCampaignId) {
      toast?.error?.("Campaign must be published to sync analytics.");
      return;
    }
    setSyncingCampaign(true);
    try {
      const res = await fetch(`/api/google_ads/campaigns/${campaign.id}/sync_insights`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data) {
        onRefetch?.();
        toast?.success?.("Campaign analytics synced.");
      } else {
        toast?.error?.(data?.error || "Failed to sync analytics.");
      }
    } catch (e) {
      console.error("Sync campaign insights error", e);
      toast?.error?.("Failed to sync analytics.");
    } finally {
      setSyncingCampaign(false);
    }
  };

  const syncAdGroup = async (adGroupId) => {
    if (!adGroupId) return;
    setSyncingAdGroupId(adGroupId);
    try {
      const res = await fetch(`/api/google_ads/ad_groups/${adGroupId}/sync_insights`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data) {
        onRefetch?.();
        toast?.success?.("Ad group analytics synced.");
      } else {
        toast?.error?.(data?.error || "Failed to sync ad group analytics.");
      }
    } catch (e) {
      console.error("Sync ad group insights error", e);
      toast?.error?.("Failed to sync ad group analytics.");
    } finally {
      setSyncingAdGroupId(null);
    }
  };

  const syncAd = async (adId) => {
    if (!adId) return;
    setSyncingAdId(adId);
    try {
      const res = await fetch(`/api/google_ads/ads/${adId}/sync_insights`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data) {
        onRefetch?.();
        toast?.success?.("Ad analytics synced.");
      } else {
        toast?.error?.(data?.error || "Failed to sync ad analytics.");
      }
    } catch (e) {
      console.error("Sync ad insights error", e);
      toast?.error?.("Failed to sync ad analytics.");
    } finally {
      setSyncingAdId(null);
    }
  };

  if (!hasGoogleCampaignId) {
    return (
      <div className="campaign-detail-page__performance-panel">
        <p className="campaign-detail-page__performance-empty">
          Publish this campaign to Google Ads to view and sync performance metrics.
        </p>
      </div>
    );
  }

  const allAds = adGroups.flatMap((ag) => (ag.ads || []).map((ad) => ({ ...ad, adGroupName: ag.name })));

  return (
    <div className="campaign-detail-page__performance-panel">
      {/* Campaign section */}
      <section className="campaign-detail-page__performance-section">
        <h4 className="campaign-detail-page__performance-section-title">Campaign performance (last 30 days)</h4>
        <div className="campaign-detail-page__performance-block campaign-detail-page__performance-block--campaign">
          <SectionHeader
            title={campaign?.name ? `“${campaign.name}”` : "Campaign"}
            lastSynced={campaignInsights?.insights_synced_at}
            onSync={syncCampaign}
            syncing={syncingCampaign}
            showSync={canSyncCampaign(campaign)}
          />
          <InsightsTable
            insights={campaignInsights}
            emptyLabel="No campaign analytics yet. Click Sync Analytics to load (last 30 days)."
          />
        </div>
      </section>

      {/* Ad groups section */}
      {adGroups.length > 0 && (
        <section className="campaign-detail-page__performance-section">
          <h4 className="campaign-detail-page__performance-section-title">Ad group performance (last 30 days)</h4>
          {adGroups.map((ag) => {
            const hasId = Boolean(ag.google_ad_group_id);
            const showSync = canSyncAdGroup(ag);
            const insights = ag.ad_group_insights ?? null;
            return (
              <div key={ag.id} className="campaign-detail-page__performance-block">
                <div className="campaign-detail-page__performance-header">
                  <div className="campaign-detail-page__performance-header-left">
                    <h5 className="campaign-detail-page__performance-block-title">{ag.name || `Ad group #${ag.id}`}</h5>
                    {insights && (
                      <span className="campaign-detail-page__performance-synced">
                        Last synced: {formatDate(insights.insights_synced_at)}
                      </span>
                    )}
                  </div>
                  {showSync && (
                    <div className="campaign-detail-page__performance-header-right">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm campaign-detail-page__sync-btn"
                        onClick={() => syncAdGroup(ag.id)}
                        disabled={syncingAdGroupId === ag.id}
                      >
                        {syncingAdGroupId === ag.id ? "Syncing…" : "Sync Analytics"}
                      </button>
                    </div>
                  )}
                </div>
                {!hasId ? (
                  <p className="campaign-detail-page__performance-empty">Publish this ad group to sync analytics.</p>
                ) : (
                  <InsightsTable
                    insights={insights}
                    emptyLabel="No ad group analytics yet. Click Sync Analytics to load (last 30 days)."
                  />
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* Ads section */}
      {allAds.length > 0 && (
        <section className="campaign-detail-page__performance-section">
          <h4 className="campaign-detail-page__performance-section-title">Ad performance (last 30 days)</h4>
          {allAds.map((ad) => {
            const hasId = Boolean(ad.google_ad_id);
            const showSync = canSyncAd(ad);
            const insights = ad.ad_insights ?? null;
            return (
              <div key={ad.id} className="campaign-detail-page__performance-block">
                <div className="campaign-detail-page__performance-header">
                  <div className="campaign-detail-page__performance-header-left">
                    <h5 className="campaign-detail-page__performance-block-title">
                      {ad.name || `Ad #${ad.id}`}
                      {ad.adGroupName && <span className="campaign-detail-page__performance-block-sub"> · {ad.adGroupName}</span>}
                    </h5>
                    {insights && (
                      <span className="campaign-detail-page__performance-synced">
                        Last synced: {formatDate(insights.insights_synced_at)}
                      </span>
                    )}
                  </div>
                  {showSync && (
                    <div className="campaign-detail-page__performance-header-right">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm campaign-detail-page__sync-btn"
                        onClick={() => syncAd(ad.id)}
                        disabled={syncingAdId === ad.id}
                      >
                        {syncingAdId === ad.id ? "Syncing…" : "Sync Analytics"}
                      </button>
                    </div>
                  )}
                </div>
                {!hasId ? (
                  <p className="campaign-detail-page__performance-empty">Publish this ad to sync analytics.</p>
                ) : (
                  <InsightsTable
                    insights={insights}
                    emptyLabel="No ad analytics yet. Click Sync Analytics to load (last 30 days)."
                  />
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
};

export default GoogleCampaignPerformancePanel;
